/**
 * SREPanel Component
 * ==================
 * DARK PREMIUM THEME
 * Core intelligence block - NOT secondary
 * Integrated, immersive, essential
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useBlueprint } from '../../context/BlueprintContext';
import { Link } from 'react-router-dom';
import { getSlabRealityAPI } from '../../services/slabRealityApi';
import { getRelatedWithSRE, getSREConfig } from '../../utils/sreEngine';
import { getMaterialImageUrl, handleImageError } from '../../utils/materialImages';
import {
  Sparkles,
  Zap,
  ChevronRight,
  Target,
  RefreshCw,
  ExternalLink,
  Eye
} from 'lucide-react';

// Score visualization - elegant
const ScoreRing = ({ score }) => {
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative w-20 h-20">
      <svg className="w-20 h-20 transform -rotate-90">
        <circle
          cx="40"
          cy="40"
          r="36"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="3"
        />
        <circle
          cx="40"
          cy="40"
          r="36"
          fill="none"
          stroke="#D6C1A5"
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-light text-white">{score}%</span>
      </div>
    </div>
  );
};

// Best match card - compact, elegant
const BestMatchCard = ({ match, buildRoute }) => {
  if (!match) return null;
  
  const materialRoute = buildRoute ? buildRoute('material_detail', { slug: match.material.id }) : `/materials/${match.material.id}`;
  
  return (
    <Link 
      to={materialRoute}
      className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all"
    >
      {/* Image */}
      <div className="w-16 h-16 overflow-hidden flex-shrink-0">
        <img
          src={getMaterialImageUrl(match.material)}
          alt={match.material.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={handleImageError}
        />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Target size={10} className="text-[#D6C1A5]" />
          <span className="text-[9px] font-bold tracking-[2px] uppercase text-[#D6C1A5]">
            Best Match
          </span>
        </div>
        <p className="font-medium text-sm text-white truncate">
          {match.material.name}
        </p>
        <p className="text-xs text-white/50">
          {match.score}% • {match.label}
        </p>
      </div>
      
      {/* Arrow */}
      <ChevronRight size={16} className="text-white/30 group-hover:text-[#D6C1A5] group-hover:translate-x-1 transition-all" />
    </Link>
  );
};

// Match reason - minimal
const MatchReason = ({ reason }) => (
  <div className="flex items-center gap-2 text-xs text-white/50">
    <div className="w-1 h-1 rounded-full bg-[#D6C1A5]" />
    <span>{reason.explanation}</span>
  </div>
);

const SREPanel = ({ material, expanded = false }) => {
  const { content, logic, getLogic, getButton, buildRoute, getRoute } = useBlueprint();
  
  // Content from config
  const panelContent = content?.material?.sre_panel || {};
  
  // SRE config
  const sreConfig = getSREConfig(logic);
  
  // State
  const [allMaterials, setAllMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(expanded);
  
  // Fetch materials
  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      try {
        const result = await getSlabRealityAPI();
        if (result.success && result.data?.materials) {
          setAllMaterials(result.data.materials);
        }
      } catch (err) {
        console.error('SREPanel fetch error:', err);
      }
      setLoading(false);
    };
    
    fetchMaterials();
  }, []);
  
  // Calculate related materials
  const relatedMaterials = useMemo(() => {
    if (!material || !allMaterials.length) return [];
    return getRelatedWithSRE(material, allMaterials, logic, 3);
  }, [material, allMaterials, logic]);
  
  // Top match
  const topMatch = relatedMaterials[0] || null;
  
  if (!material) return null;
  
  // Loading state
  if (loading) {
    return (
      <div 
        className="bg-[#0A0A09] border border-white/5 p-8"
        data-testid="sre-panel"
      >
        <div className="flex items-center justify-center py-8">
          <RefreshCw size={20} className="text-[#D6C1A5] animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="bg-[#0A0A09] border border-white/5 overflow-hidden"
      data-testid="sre-panel"
    >
      {/* Header - Premium */}
      <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <Sparkles size={16} className="text-[#D6C1A5]" />
          </div>
          <div>
            <h3 className="text-[11px] font-bold tracking-[3px] uppercase text-[#D6C1A5]">
              {panelContent.title || 'SRE Intelligence'}
            </h3>
          </div>
        </div>
        
        {/* Score Ring */}
        {topMatch && (
          <ScoreRing score={topMatch.score} />
        )}
      </div>
      
      {/* Best Match */}
      <div className="p-4">
        <BestMatchCard match={topMatch} buildRoute={buildRoute} />
      </div>
      
      {/* Match Reasons */}
      {topMatch?.reasons?.length > 0 && (
        <div className="px-6 pb-4 space-y-2">
          {topMatch.reasons.slice(0, 3).map((reason, i) => (
            <MatchReason key={i} reason={reason} />
          ))}
        </div>
      )}
      
      {/* Other Matches - Collapsed */}
      {relatedMaterials.length > 1 && (
        <div className="border-t border-white/5">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-6 py-3 flex items-center justify-between text-xs text-white/40 hover:text-white/60 transition-colors"
          >
            <span className="tracking-wide">
              {relatedMaterials.length - 1} altri match
            </span>
            <ChevronRight size={14} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
          
          {isExpanded && (
            <div className="px-4 pb-4 space-y-2">
              {relatedMaterials.slice(1).map((item) => (
                <Link
                  key={item.material.id}
                  to={buildRoute('material_detail', { slug: item.material.id })}
                  className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="w-10 h-10 overflow-hidden flex-shrink-0">
                    <img
                      src={getMaterialImageUrl(item.material)}
                      alt={item.material.name}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{item.material.name}</p>
                    <p className="text-xs text-white/40">{item.score}%</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="p-4 border-t border-white/5">
        <div className="flex gap-3">
          <Link
            to={`${getRoute('explorer', '/materials')}?similar_to=${material.id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#D6C1A5] text-[#0A0A09] text-xs font-semibold tracking-wide hover:bg-[#E0CBB5] transition-colors"
          >
            <Eye size={14} />
            {panelContent.find_similar || 'Trova Simili'}
          </Link>
          <Link
            to={`${getRoute('compare', '/compare/materials')}?ids=${material.id},${topMatch?.material?.id || ''}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/10 text-white/70 text-xs font-medium tracking-wide hover:bg-white/5 hover:text-white transition-colors"
          >
            <Zap size={14} />
            {getButton('compare', 'Confronta')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SREPanel;
